import React, { useState } from "react";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSModalZIndex,
} from "@/mds";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { APP_CONFIG } from "@/domains/global/config";
import { windowModule } from "@/modules/window";
import { noop } from "lodash-es";
import { observer } from "mobx-react-lite";

export interface SyncErrorOverlayContentProps {
  isModalOpen: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  resetActionLabel?: string | null;
  extraActionButtons?: React.ReactNode;
  includeContactSupportButton?: boolean;
  lastSyncActionTime?: string;
  /** This can be sync or async. */
  handleResetClick: (() => Promise<void>) | (() => void);
}

export const SyncErrorOverlayContent = observer(function SyncErrorOverlayContent({
  isModalOpen,
  title,
  message,
  resetActionLabel,
  extraActionButtons,
  includeContactSupportButton = true,
  lastSyncActionTime = "",
  handleResetClick: handleClick,
}: SyncErrorOverlayContentProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleResetClick = async () => {
    setIsLoading(true);
    try {
      await Promise.resolve(handleClick());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MDSModalDialog
      isDismissable={false}
      isOpen={isModalOpen}
      handleClose={noop}
      zIndex={MDSModalZIndex.AppStoreCrashNotice}
    >
      {title && (
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          {title}
        </MDSText>
      )}

      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          {message ??
            "Mem hasn't been able to sync your recent activity. In order to prevent future data loss, please reset syncing."}
        </MDSText>
        {lastSyncActionTime && (
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
            {`By resetting syncing, changes made since ${lastSyncActionTime} will be undone.`}
          </MDSText>
        )}
      </MDSDialogTextContent>

      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        {includeContactSupportButton && (
          <MDSButton
            onClick={() => {
              windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.HELP_AND_SUPPORT) });
            }}
          >
            Contact Support
          </MDSButton>
        )}
        {extraActionButtons}
        {resetActionLabel !== null && (
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={handleResetClick}
            isLoading={isLoading}
          >
            {resetActionLabel ?? "Reset Syncing"}
          </MDSButton>
        )}
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});
